// Ported from the native app's MorePageView.swift — notification center,
// Settings page: notification center, Live Alerts, Connections,
// Disclaimer, Help & Support (bottom).
// LaunchAtLoginToggle has no browser equivalent (the extension's background
// worker runs whenever the browser does), so it's intentionally absent.

import { el, spacer, divider, svgIcon, pressable, closeButton, openConnectionsWindow } from "../lib/dom.js";
import { getDataMode, setDataMode, type DataMode } from "../lib/data-mode.js";
import { storageGet, storageSet } from "../lib/storage.js";
import { notificationStore, categoryIcon, type AppNotification } from "../lib/notifications-store.js";
import { resourcesStore } from "../lib/resources-store.js";
import { fetchServerStatus } from "../lib/trading-server.js";
import { allProviders, providerById, selectedProviderId } from "../lib/data-providers/registry.js";
import { pushSupported, pushStatus, enablePush, disablePush, sendTestPush, syncArmedAlerts, clearArmedAlerts, syncLiveSetups, clearLiveSetups, type ArmedAlert } from "../lib/web-push.js";
import { platform } from "../lib/platform/index.js";
import { getStoredLicense, licenseState, type LicenseState } from "../lib/license.js";
import { renderLicenseGate } from "../lib/license-gate.js";

const icons = {
  close: "M4 4l8 8M12 4l-8 8",
  chevronDown: "M4 6l4 4 4-4",
  chevronUp: "M4 10l4-4 4 4",
  chevronRight: "M6 4l4 4-4 4",
  arrowUpRight: "M4 12L12 4M12 4H6.5M12 4v5.5",
  bellSlash: "M8 2a4 4 0 0 0-4 4v3L2.5 11.5h11L12 9V6a4 4 0 0 0-4-4zM6.5 13.5a1.5 1.5 0 0 0 3 0M2 2l12 12",
  // Setup tile glyphs (16 viewBox, stroked).
  link: "M6.8 9.2a2.4 2.4 0 0 0 3.4 0l1.6-1.6a2.4 2.4 0 0 0-3.4-3.4l-.8.8M9.2 6.8a2.4 2.4 0 0 0-3.4 0L4.2 8.4a2.4 2.4 0 0 0 3.4 3.4l.8-.8",
  doc: "M4.5 2.5h4l3 3v8h-7zM8.5 2.5v3h3M6 8.5h4M6 10.5h4",
  help: "M8 2a6 6 0 1 0 0 12A6 6 0 0 0 8 2M6.4 6.4a1.6 1.6 0 0 1 3 .5c0 1-1.4 1.3-1.4 2.2M8 11.2v.2",
  tick: "M3 8.5l3 3 6-7",
  journal: "M8 4C6.6 3.1 4.4 3.1 2.8 3.6v8c1.6-.5 3.8-.5 5.2.4M8 4c1.4-.9 3.6-.9 5.2-.4v8c-1.6-.5-3.8-.5-5.2.4M8 4v8",
};

// Journal is a real page kept off the bottom bar (D-086); it lives at nav index
// 1 and is reached from here.
const JOURNAL_PAGE = 1;

// The Connections checklist (D-084): each service is red with an ✕ until it's
// connected, then green with a ✓.
const CONN_ITEMS: Array<{ key: string; label: string }> = [
  { key: "feeds", label: "Data feeds" },
  { key: "keys", label: "API keys" },
  { key: "server", label: "Trading Server" },
];

const categoryColor: Record<AppNotification["category"], string> = {
  productUpdate: "var(--up)",
  watchlistExtreme: "#bf5af2",
  econEvent: "var(--up)",
  sessionChange: "#5e5ce6",
  priceAlert: "var(--accent-yellow)",
  strategySignal: "var(--up)",
};

const disclaimerBullets = [
  "Not financial advice. Nothing in this app — watchlist indicators or journal stats — is investment advice, a recommendation, or a solicitation to buy or sell any security or futures contract.",
  "Past performance does not predict future results. Historical win rate, R-multiple, and Bollinger Band patterns shown here are descriptive, not predictive.",
  "Trading is risky. Futures and other instruments involve substantial risk of loss, amplified by leverage. Only risk capital you can afford to lose.",
  "Do your own research. Consult a licensed financial advisor before making trading decisions.",
  "No liability. The developer assumes no responsibility for trading losses, missed opportunities, or decisions made using this app.",
  "Technical patterns, not predictions. The Bollinger Band price-position indicator reflects where price sits in a statistical range — it is not a buy or sell signal.",
  "Third-party data. Prices and market data used by this app come from outside sources and may be delayed, incomplete, or inaccurate.",
  "No uptime guarantee. Data feeds and this app's availability may be interrupted at any time.",
  "Affiliate disclosure. Some links in this app may be affiliate or referral links. TickAhead may earn a commission if you open an account through them, at no extra cost to you. This never influences the data, notifications, or content shown elsewhere in the app.",
];

function timeAgo(epochMs: number): string {
  const seconds = Math.round((epochMs - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat("en", { style: "narrow" });
  const abs = Math.abs(seconds);
  if (abs < 60) return "now";
  if (abs < 3600) return rtf.format(Math.trunc(seconds / 60), "minute");
  if (abs < 86400) return rtf.format(Math.trunc(seconds / 3600), "hour");
  return rtf.format(Math.trunc(seconds / 86400), "day");
}

// Accordion expansion survives page flips within one popup lifetime.
const ui = {
  notificationsExpanded: true,
  resourcesExpanded: false,
  showDisclaimer: false,
  showSupport: false,
};

// Help & Support is a short in-app FAQ; anything deeper links out to the
// website help centre + email. The in-app answer assistant was retired to
// keep the app lean (D-079).
const FAQS: Array<[string, string]> = [
  ["Where did the Markets and Clock pages go?", "They're on Monitor now, as their own Clock and Calendar sections in the accordion. Tip: click the pin on any section header to move it to the top and keep it open."],
  ["What are the Watch, Tune and Deploy tabs?", "Watch is your instrument-agnostic readings — add securities and read them across your whole list. Tune is the instrument-tuned Strategy Lab: open a security on Watch and you land here to design a strategy for it — build your own, or improve/discover/reconstruct/learn from your trades — and backtest it. Deploy is where a strategy goes live on your trading server."],
  ["Why aren't the candles green and red?", "Candles are neutral grey — solid light-grey bodies for up bars, solid dark-grey for down. That removes the emotional pull of red/green and stays readable if you're colour-blind; direction is clear from the shade (lighter = up, darker = down)."],
  ["What do the coloured markers on the Watchlist sliders mean?", "They show where a reading sits against the bounds you set: blue in your low zone, purple in your high zone, grey in neither. It's never a buy or sell call — you decide what each zone means for your setup."],
  ["What happens when I click a security?", "It opens that instrument's strategy builder — Setups, Build and Confirm — where you author the entry conditions, size and trade-management for a strategy, then test, deploy and monitor it. There's no chart in this version; you work from the readings and the builder. Each security keeps its own strategy, so coming back to it restores your work."],
  ["Where do my journal trades come from?", "You don't log them by hand — the Journal fills automatically from your deployed strategies. Deploy a strategy (to your own PC, a server, or your Pi) and every trade it takes lands in the Journal with its prices, R and result, so you review what your strategy actually did."],
  ["How do I set up a live alert?", "Use the bell icon on a security's Watch row to set a price alert. Armed alerts appear on Monitor under Armed alerts, where you can cancel them. Alerts fire as a system notification when price crosses."],
  ["Is any of this financial advice?", "No. Everything in TickAhead is descriptive, not advice — indicators, stats, and colours included. See the full Disclaimer in Setup."],
];

let storesInitialized = false;

/** Opens Connections as a box over a chart (never the side panel).
 *  1. If a chart tab already has the box open → jump to that tab.
 *  2. Else if a chart is open → show it there (prefer the active chart).
 *  3. Else → open a blank "no security" chart that hosts the box. */
// v1 (D-133): Connections opens as its own full-window tab (no chart) — the wide
// surface it had before, consistent with the builder tab.
function openConnections(): void {
  // Web shell: Connections is an in-app route (#/connections), not a new window.
  // The extension opens its own Connections tab.
  if (platform.kind === "web") {
    location.hash = "#/connections";
    return;
  }
  openConnectionsWindow();
}

export async function renderMorePage(root: HTMLElement): Promise<() => void> {
  if (!storesInitialized) {
    storesInitialized = true;
    await Promise.all([notificationStore.init(), resourcesStore.init()]);
  }

  const container = el("div", { style: { display: "flex", flexDirection: "column", gap: "12px", height: "100%", position: "relative" } });
  root.replaceChildren(container);

  // Live connection status for the checklist. null = still loading.
  const connStatus: Record<string, boolean | null> = { feeds: null, keys: null, server: null };
  async function refreshConnStatus(): Promise<void> {
    // "Data feeds" = a live/own feed is selected (a real-time provider); the
    // free public feed is delayed, so it counts as not yet connected.
    try { const sel = providerById(await selectedProviderId()); connStatus.feeds = !!sel?.realtime; } catch { connStatus.feeds = false; }
    let keys = false;
    for (const p of allProviders) {
      if (!p.byo) continue;
      try {
        const c = await p.byo.getCreds();
        if (c && Object.values(c).some((v) => !!v)) { keys = true; break; }
      } catch { /* ignore */ }
    }
    connStatus.keys = keys;
    try { const st = await fetchServerStatus(); connStatus.server = !!st?.paired; } catch { connStatus.server = false; }
    render();
  }

  function notificationBubble(notification: AppNotification): HTMLElement {
    const bubble = el(
      "div",
      {
        style: {
          display: "flex",
          gap: "10px",
          alignItems: "flex-start",
          padding: "10px",
          background: "var(--bg-fill)",
          borderRadius: "10px",
          cursor: "pointer",
        },
        onClick: () => {
          window.dispatchEvent(new CustomEvent("tickahead:navigate", { detail: notification.targetPage }));
        },
      },
      [
        el("div", {
          className: "icon-badge",
          text: categoryIcon[notification.category],
          style: { background: categoryColor[notification.category], fontSize: "12px" },
        }),
        el("div", { style: { display: "flex", flexDirection: "column", gap: "3px", flex: "1", minWidth: "0" } }, [
          el("div", { text: notification.title, style: { font: "var(--font-header)" } }),
          el("div", { text: notification.message, style: { font: "var(--font-body)", color: "var(--text-secondary)" } }),
          el("div", { text: timeAgo(notification.date), style: { font: "var(--font-caption)", color: "var(--text-secondary-faint)" } }),
        ]),
      ]
    );
    const dismissBtn = closeButton(() => {}, { label: "Dismiss", size: 11 });
    Object.assign(dismissBtn.style, { width: "18px", height: "18px" });
    dismissBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      void notificationStore.dismiss(notification.id);
    });
    bubble.appendChild(dismissBtn);
    return bubble;
  }

  function supportOverlay(): HTMLElement {
    const card = el("div", {
      style: {
        position: "absolute",
        inset: "0",
        background: "var(--bg)",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        zIndex: "10",
      },
    });
    const closeBtn = closeButton(() => {}, { label: "Close support", size: 14 });
    closeBtn.addEventListener("click", () => {
      ui.showSupport = false;
      render();
    });
    card.appendChild(
      el("div", { style: { display: "flex", alignItems: "center" } }, [
        el("div", { text: "Help & Support", style: { font: "var(--font-title)" } }),
        spacer(),
        closeBtn,
      ])
    );

    // A short in-app FAQ answers the common questions instantly, offline, in
    // the panel. Everything deeper lives on the website help centre + email.
    card.appendChild(el("div", { text: "Frequently asked", style: { font: "var(--font-label)", color: "var(--text-secondary)", letterSpacing: "0.06em", textTransform: "uppercase", flex: "none" } }));
    const list = el("div", { className: "scroll", style: { display: "flex", flexDirection: "column", flex: "1" } });
    for (const [q, a] of FAQS) {
      const answer = el("div", { text: a, style: { font: "var(--font-body)", color: "var(--text-secondary)", padding: "0 2px 10px", display: "none" } });
      const chev = svgIcon(icons.chevronDown, 10);
      chev.style.transition = "transform .12s ease";
      const qBtn = el("button", {
        style: { display: "flex", alignItems: "center", gap: "8px", width: "100%", textAlign: "left", padding: "9px 2px", color: "var(--text-primary)" },
        onClick: () => {
          const open = answer.style.display === "none";
          answer.style.display = open ? "" : "none";
          chev.style.transform = open ? "rotate(180deg)" : "";
        },
      }, [el("div", { text: q, style: { font: "var(--font-body)", flex: "1" } }), chev]);
      list.appendChild(el("div", { style: { display: "flex", flexDirection: "column", borderBottom: "1px solid var(--border)" } }, [qBtn, answer]));
    }
    card.appendChild(list);

    const helpLink = el("a", {
      text: "Full help centre at tickahead.com/help →",
      attrs: { href: "https://tickahead.com/help", target: "_blank", rel: "noopener" },
    });
    Object.assign(helpLink.style, { font: "var(--font-button)", color: "var(--accent-blue)", textDecoration: "none", padding: "8px 2px 2px", flex: "none" });
    card.appendChild(helpLink);

    const emailLink = el("a", {
      text: "Email support →",
      attrs: { href: "mailto:contact@tickahead.com?subject=" + encodeURIComponent("[TickAhead] Support") },
    });
    Object.assign(emailLink.style, { font: "var(--font-caption)", color: "var(--accent-blue)", textDecoration: "none", padding: "0 2px 4px", flex: "none" });
    card.appendChild(emailLink);
    return card;
  }

  function disclaimerOverlay(): HTMLElement {
    const card = el("div", {
      style: {
        position: "absolute",
        inset: "0",
        background: "var(--bg)",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        zIndex: "10",
      },
    });
    const closeBtn = closeButton(() => {}, { label: "Close disclaimer", size: 14 });
    closeBtn.addEventListener("click", () => {
      ui.showDisclaimer = false;
      render();
    });
    card.appendChild(
      el("div", { style: { display: "flex", alignItems: "center" } }, [
        el("div", { text: "Disclaimer", style: { font: "var(--font-title)" } }),
        spacer(),
        closeBtn,
      ])
    );
    const body = el("div", { className: "scroll", style: { display: "flex", flexDirection: "column", gap: "10px", flex: "1" } });
    body.appendChild(
      el("div", { style: { display: "flex", gap: "8px", alignItems: "center" } }, [
        el("span", { text: "⚠️" }),
        el("div", { text: "Please read before using TickAhead", style: { font: "var(--font-header)" } }),
      ])
    );
    for (const bullet of disclaimerBullets) {
      body.appendChild(
        el("div", { style: { display: "flex", gap: "8px", alignItems: "flex-start" } }, [
          el("div", { style: { width: "4px", height: "4px", borderRadius: "50%", background: "rgba(154,154,158,0.4)", marginTop: "5px", flex: "none" } }),
          el("div", { text: bullet, style: { font: "var(--font-body)", color: "var(--text-secondary)" } }),
        ])
      );
    }
    body.appendChild(divider());
    const contact = el("div", { style: { display: "flex", gap: "4px", font: "var(--font-caption)", color: "var(--text-secondary)" } }, [
      el("span", { text: "Questions?" }),
    ]);
    const mail = el("a", { text: "contact@tickahead.com", attrs: { href: "mailto:contact@tickahead.com" } });
    mail.style.color = "var(--accent-blue)";
    contact.appendChild(mail);
    body.appendChild(contact);
    card.appendChild(body);
    return card;
  }

  // In-app sign-in (web). The paywall ships off, so there's no gate screen on
  // launch — but tying push/alerts to an account still needs a login. This opens
  // the same license gate as a full-screen overlay; on success, `onDone` re-renders.
  function openSignIn(onDone: () => void): void {
    const overlay = el("div", { style: { position: "fixed", inset: "0", background: "var(--bg)", zIndex: "60", display: "flex", flexDirection: "column" } });
    overlay.appendChild(el("div", { style: { display: "flex", justifyContent: "flex-end", padding: "8px" } }, [closeButton(() => overlay.remove())]));
    const host = el("div", { style: { flex: "1", minHeight: "0" } });
    overlay.appendChild(host);
    document.body.appendChild(overlay);
    void licenseState().then((st) => {
      const gateState: Exclude<LicenseState, "active"> = st === "active" ? "unlicensed" : st;
      renderLicenseGate(host, gateState, () => {
        overlay.remove();
        onDone();
      });
    });
  }

  // Web-only "Phone alerts" control: enable/disable the PWA push subscription
  // and fire a test push. Builds synchronously, then fills its state async.
  function pushSection(): HTMLElement {
    const title = el("div", { text: "Phone alerts", style: { font: "var(--font-body)", color: "var(--text-primary)" } });
    const sub = el("div", { text: "Get watchlist alerts on this device, even when the app is closed.", style: { font: "var(--font-caption)", color: "var(--text-secondary)", maxWidth: "240px", textAlign: "center" } });
    const btn = el("button", { className: "pill", text: "…" }) as HTMLButtonElement;
    const testBtn = el("button", { className: "pill", text: "Send test" }) as HTMLButtonElement;
    const msg = el("div", { style: { font: "var(--font-caption)", color: "var(--text-secondary)", minHeight: "14px", textAlign: "center", maxWidth: "240px" } });
    const wrap = el("div", { style: { display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", paddingBottom: "10px" } }, [
      title,
      sub,
      el("div", { style: { display: "flex", gap: "8px" } }, [btn, testBtn]),
      msg,
    ]);

    let signedIn = false;
    async function refresh(): Promise<void> {
      btn.disabled = false;
      signedIn = !!(await getStoredLicense())?.token;
      if (!signedIn) {
        // Alerts are per-account — sign in before offering to turn them on.
        btn.textContent = "Sign in";
        testBtn.style.display = "none";
        msg.textContent = "";
        return;
      }
      const st = await pushStatus();
      testBtn.style.display = st === "on" ? "" : "none";
      if (st === "denied") {
        btn.textContent = "Blocked";
        btn.disabled = true;
        msg.textContent = "Enable notifications for this site in your browser settings.";
        return;
      }
      btn.textContent = st === "on" ? "Turn off" : "Turn on";
    }
    btn.addEventListener("click", () => {
      if (!signedIn) {
        openSignIn(() => void refresh());
        return;
      }
      btn.disabled = true;
      msg.textContent = "Working…";
      void (async () => {
        try {
          if ((await pushStatus()) === "on") {
            await disablePush();
            await clearArmedAlerts(); // stop the server scanning once alerts are off
            await clearLiveSetups();
            msg.textContent = "";
          } else {
            const r = await enablePush();
            if (r.ok) {
              // Now opted in — seed the server with any already-armed alerts + live setups.
              const armed = await storageGet<Record<string, ArmedAlert>>("watchlistAlerts", {});
              await syncArmedAlerts(Object.values(armed));
              const working = await storageGet<Array<{ id: string; sym: string }>>("wlWorking", []);
              await syncLiveSetups(working.map((c) => ({ id: c.id, symbol: c.sym, card: c })));
            }
            msg.textContent = r.ok ? "" : r.message ?? "Couldn't turn on alerts.";
          }
        } catch (e) {
          msg.textContent = e instanceof Error ? e.message : "Something went wrong.";
        } finally {
          await refresh();
        }
      })();
    });
    testBtn.addEventListener("click", () => {
      msg.textContent = "Sending…";
      void sendTestPush().then((r) => {
        msg.textContent = r.ok ? (r.sent ? "Sent." : "No devices to send to yet.") : r.message ?? "Test failed.";
      });
    });
    void refresh();
    return wrap;
  }

  function render() {
    container.replaceChildren();

    // Free / Premium data mode (D-152). Free = built-in Yahoo (stocks only, no
    // setup). Premium = your own live data / broker connections; flipping to
    // Premium opens the Connections overlay (the old button's behaviour). The
    // connection checklist shows underneath in Premium so you can see what's set.
    const checklist = el("div", { style: { display: "flex", flexDirection: "column", gap: "9px", width: "100%", marginTop: "2px" } });
    for (const item of CONN_ITEMS) {
      const ok = connStatus[item.key] === true;
      const statusEl = el("div", { style: { color: ok ? "#2e9e6a" : "var(--text-secondary-faint)", display: "flex", flex: "none" } }, [svgIcon(ok ? icons.tick : icons.close, ok ? 14 : 12)]);
      checklist.appendChild(el("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "20px" } }, [
        el("div", { text: item.label, style: { font: "var(--font-body)", color: ok ? "var(--text-primary)" : "var(--text-secondary-faint)" } }),
        statusEl,
      ]));
    }

    const caption = el("div", { style: { font: "var(--font-caption)", color: "var(--text-secondary)", maxWidth: "230px", textAlign: "center" } });
    const seg = el("div", { style: { display: "inline-flex", gap: "2px", background: "var(--bg-fill)", padding: "3px", borderRadius: "999px" } });
    let curMode: DataMode = "free";
    const applyMode = (): void => {
      caption.textContent = curMode === "free"
        ? "Free — stock data from Yahoo Finance. Nothing to set up."
        : "Premium — build with your own live data and broker connections.";
      checklist.style.display = curMode === "premium" ? "flex" : "none";
      seg.replaceChildren(...(["free", "premium"] as DataMode[]).map((m) => {
        const on = curMode === m;
        const b = el("button", {
          text: m === "free" ? "Free" : "Premium",
          style: { background: on ? "var(--bg)" : "transparent", color: on ? "var(--text-primary)" : "var(--text-secondary)", font: "inherit", fontWeight: on ? "600" : "500", padding: "7px 20px", borderRadius: "999px", cursor: "pointer", boxShadow: on ? "0 1px 2px rgba(0,0,0,.10)" : "none" },
        });
        b.addEventListener("click", () => {
          const already = curMode === m;
          curMode = m;
          void setDataMode(m);
          applyMode();
          // Premium (whether newly selected or re-clicked) opens Connections.
          if (m === "premium") openConnections();
          void already;
        });
        return b;
      }));
    };
    applyMode();
    void getDataMode().then((m) => { curMode = m; applyMode(); });

    const connCard = el("div", {
      style: { display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", padding: "20px 22px", minWidth: "216px", background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "16px", color: "var(--text-primary)" },
    }, [
      el("div", { style: { width: "44px", height: "44px", borderRadius: "13px", background: "var(--bg-fill)", display: "flex", alignItems: "center", justifyContent: "center" } }, [svgIcon(icons.link, 23)]),
      el("div", { text: "Data", style: { font: "var(--font-title)" } }),
      seg,
      caption,
      checklist,
    ]);
    // Phone alerts (D-136) — web/PWA only. The extension's background worker
    // already delivers alerts locally, so this appears only in the web shell,
    // where push is the way to reach a closed app / a phone. It sits directly
    // under the Connections card (both centred together, scrollable if tall).
    const centered: HTMLElement[] = [connCard];
    if (pushSupported()) centered.push(pushSection());
    container.appendChild(el("div", { style: { flex: "1", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "22px", minHeight: "0", overflowY: "auto", padding: "12px 0" } }, centered));

    // Disclaimer + Help & Support are secondary — small icon-only buttons in the
    // top-right corner, in the chart tool-panel pill style.
    const cornerPill = (icon: string, tip: string, onClick: () => void): HTMLElement => {
      const b = el("button", {
        className: "pill",
        attrs: { title: tip, "aria-label": tip },
        style: { width: "30px", height: "30px", padding: "0", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-secondary)" },
        onClick,
      }, [svgIcon(icon, 15)]);
      return pressable(b);
    };
    container.appendChild(el("div", { style: { position: "absolute", right: "4px", top: "4px", display: "flex", gap: "6px", zIndex: "2" } }, [
      // Journal moved to the chart toolbar; reached from there now.
      cornerPill(icons.doc, "Disclaimer", () => { ui.showDisclaimer = true; render(); }),
      cornerPill(icons.help, "Help & Support", () => { ui.showSupport = true; render(); }),
    ]));

    if (ui.showDisclaimer) container.appendChild(disclaimerOverlay());
    if (ui.showSupport) container.appendChild(supportOverlay());
  }

  render();
  void refreshConnStatus(); // fills the checklist once statuses resolve
  const unsubs = [notificationStore.changed.subscribe(render), resourcesStore.changed.subscribe(render)];
  return () => unsubs.forEach((u) => u());
}
